import React from "react";
import { Autocomplete, Grid, styled } from "@mui/material";
import { TextField, Typography } from "@platform/design-system";

import { CLIENT_TYPES, CLIENT_FORM } from "../constants/client";

const {
  NAME,
  TOTAL_BENEFITS,
  STREET_1,
  STREET_2,
  CITY,
  STATE,
  SITUS_STATE,
  ZIP_CODE,
  CLIENT_EXECUTIVE,
  PRACTICE,
  CORPORATE_ENTITY,
  ONEDIGITAL_OFFICE,
  OFFICE,
  PRODUCER_FIRST_NAME,
  PRODUCER_LAST_NAME,
  PRODUCER_EMAIL,
  CLIENT_TYPE,
  CLIENT_ID,
} = CLIENT_FORM;

const StyledGrid = styled(Grid, {
  shouldForwardProp: (prop) => !["fromClients"].includes(prop),
})(({ theme, fromClients }) => ({
  backgroundColor: fromClients ? "white" : "transparent",
  ...(fromClients && {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(6),
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(0),
  }),
}));

const ClientForm = ({
  client,
  onChange,
  states,
  brokerClientInfoConfig,
  brokerClientInfoAvailableOptions,
  oneDigitalOfficeLocations,
  showClientInformationLabel = false,
}) => {
  const isCurrentClient =
    client[CLIENT_TYPE] === CLIENT_TYPES.CURRENT_CLIENT && client[CLIENT_ID];

  const handleBrokerInfOptionsChange = (name, value) => {
    if (name === ONEDIGITAL_OFFICE) {
      onChange({ target: { name, value: value.location } });
      return;
    }
    onChange({ target: { name, value } });
  };

  const selectedOneDigitalOffice =
    oneDigitalOfficeLocations?.find(
      (item) => item.location === client[ONEDIGITAL_OFFICE],
    ) || null;
  return (
    <StyledGrid fromClients container spacing={3} maxWidth="100%">
      {!isCurrentClient && (
        <Grid size={12}>
          <Typography variant="body-large">
            Provide the following information to create a new Client—
          </Typography>
        </Grid>
      )}
      {showClientInformationLabel ? (
        <Grid size={12}>
          <Typography variant="body-large">
            <strong>Client Information</strong>
          </Typography>
        </Grid>
      ) : null}
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={NAME}
          value={client[NAME]}
          onChange={onChange}
          error={!!client.errors[NAME]}
          helperText={client.errors[NAME]}
          fullWidth
          required
          label="Client name"
          autoComplete="off"
          slotProps={{ htmlInput: { "data-testid": "client-name" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={TOTAL_BENEFITS}
          value={client[TOTAL_BENEFITS]}
          onChange={onChange}
          error={!!client.errors[TOTAL_BENEFITS]}
          helperText={client.errors[TOTAL_BENEFITS]}
          fullWidth
          required
          label="Total Benefits Eligible Employees"
          autoComplete="off"
          slotProps={{ htmlInput: { "data-testid": "client-total-benefits" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Autocomplete
          data-testid="client-situs-state"
          name={SITUS_STATE}
          value={client[SITUS_STATE]}
          onChange={(_, newValue) =>
            onChange({ target: { name: SITUS_STATE, value: newValue } })
          }
          fullWidth
          options={states}
          getOptionLabel={({ name }) => name || ""}
          isOptionEqualToValue={(option, value) =>
            option.name === value.name ||
            option.abbreviation === value.abbreviation
          }
          filterOptions={filterStateOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              size="sm"
              error={!!client.errors[SITUS_STATE]}
              helperText={client.errors[SITUS_STATE]}
              required
              label="Situs State"
              slotProps={{
                ...params.inputProps,
                autoComplete: "none",
              }}
            />
          )}
        />
        <Grid size={12} marginLeft={"3px"}>
          <Typography variant="subtitle2" color="textSecondary">
            The state where a policy contract is issued or location of the
            policy owner
          </Typography>
        </Grid>
      </Grid>
      <Grid size={12}>
        <Typography variant="body-large">
          <strong>Headquarters Address</strong>
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <input
          autoComplete="off"
          name="Name"
          type="text"
          style={{ display: "none" }}
        />
        <TextField
          name={STREET_1}
          value={client[STREET_1]}
          onChange={onChange}
          error={!!client.errors[STREET_1]}
          helperText={client.errors[STREET_1]}
          fullWidth
          required
          label="Street 1"
          autoComplete="none"
          slotProps={{ htmlInput: { "data-testid": "client-street-1" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={STREET_2}
          value={client[STREET_2]}
          onChange={onChange}
          fullWidth
          label="Street 2"
          autoComplete="none"
          slotProps={{ htmlInput: { "data-testid": "client-street-2" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={CITY}
          value={client[CITY]}
          onChange={onChange}
          fullWidth
          required
          label="City"
          autoComplete="none"
          slotProps={{ htmlInput: { "data-testid": "client-city" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Autocomplete
          data-testid="client-state"
          name={STATE}
          value={client[STATE]}
          onChange={(_, newValue) =>
            onChange({ target: { name: "state", value: newValue } })
          }
          fullWidth
          options={states}
          getOptionLabel={({ name }) => name || ""}
          isOptionEqualToValue={(option, value) =>
            option.name === value.name ||
            option.abbreviation === value.abbreviation
          }
          filterOptions={filterStateOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              size="sm"
              error={!!client.errors[STATE]}
              helperText={client.errors[STATE]}
              required
              label="State"
              slotProps={{
                ...params.inputProps,
                autoComplete: "none",
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={ZIP_CODE}
          value={client[ZIP_CODE]}
          onChange={onChange}
          error={!!client.errors[ZIP_CODE]}
          helperText={client.errors[ZIP_CODE]}
          fullWidth
          required
          label="Zip Code"
          autoComplete="none"
          slotProps={{ htmlInput: { "data-testid": "client-zip-code" } }}
        />
      </Grid>
      <Grid size={12}>
        <Typography variant="body-large">
          <strong>Brokerage Information</strong>
        </Typography>
      </Grid>
      <Grid size={12}>
        <Typography variant="body-medium">
          <strong>Producer</strong>
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={PRODUCER_FIRST_NAME}
          value={client[PRODUCER_FIRST_NAME]}
          onChange={onChange}
          fullWidth
          required
          label={"First Name"}
          autoComplete="off"
          slotProps={{
            htmlInput: { "data-testid": "client-producer-first-name" },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={PRODUCER_LAST_NAME}
          value={client[PRODUCER_LAST_NAME]}
          onChange={onChange}
          fullWidth
          required
          label={"Last Name"}
          autoComplete="off"
          slotProps={{
            htmlInput: { "data-testid": "client-producer-last-name" },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          name={PRODUCER_EMAIL}
          value={client[PRODUCER_EMAIL]}
          onChange={onChange}
          error={!!client.errors[PRODUCER_EMAIL]}
          helperText={client.errors[PRODUCER_EMAIL]}
          fullWidth
          required
          label={"Email Address"}
          autoComplete="off"
          slotProps={{ htmlInput: { "data-testid": "client-producer-email" } }}
        />
      </Grid>

      <CustomizedFields
        client={client}
        onChange={onChange}
        brokerClientInfoConfig={brokerClientInfoConfig}
        brokerClientInfoAvailableOptions={brokerClientInfoAvailableOptions}
        oneDigitalOfficeLocations={oneDigitalOfficeLocations}
        selectedOneDigitalOffice={selectedOneDigitalOffice}
        handleBrokerInfOptionsChange={handleBrokerInfOptionsChange}
      />
    </StyledGrid>
  );
};

const CustomizedFields = ({
  client,
  onChange,
  brokerClientInfoConfig,
  brokerClientInfoAvailableOptions,
  oneDigitalOfficeLocations,
  selectedOneDigitalOffice,
  handleBrokerInfOptionsChange,
}) => {
  const customizedFieldKeys = [
    CLIENT_EXECUTIVE,
    PRACTICE,
    CORPORATE_ENTITY,
    OFFICE,
    ONEDIGITAL_OFFICE,
  ];
  const hasCustomizedFields = customizedFieldKeys.some(
    (key) => brokerClientInfoConfig[key],
  );

  if (!hasCustomizedFields) return null;

  return (
    <>
      <Grid size={12}>
        <Typography variant="body-large">
          <strong>Customized Fields</strong>
        </Typography>
      </Grid>

      {!!brokerClientInfoConfig[CLIENT_EXECUTIVE] && (
        <Grid size={{ xs: 12, md: 6 }} data-testid="client-custom-field">
          <TextField
            name={CLIENT_EXECUTIVE}
            value={client[CLIENT_EXECUTIVE]}
            onChange={onChange}
            error={!!client.errors[CLIENT_EXECUTIVE]}
            helperText={client.errors[CLIENT_EXECUTIVE]}
            fullWidth
            required
            label="Client Executive"
            autoComplete="off"
          />
        </Grid>
      )}
      {!!brokerClientInfoConfig[PRACTICE] && (
        <Grid size={{ xs: 12, md: 6 }} data-testid="client-custom-field">
          <Autocomplete
            name={PRACTICE}
            value={client[PRACTICE]}
            onChange={(_, option) =>
              handleBrokerInfOptionsChange(PRACTICE, option)
            }
            options={brokerClientInfoAvailableOptions?.practice || []}
            getOptionLabel={({ name }) => name || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                size="sm"
                error={!!client.errors[PRACTICE]}
                helperText={client.errors[PRACTICE]}
                required
                label="Practice"
                slotProps={{
                  ...params.inputProps,
                  autoComplete: "none",
                }}
              />
            )}
          />
        </Grid>
      )}
      {!!brokerClientInfoConfig[CORPORATE_ENTITY] && (
        <Grid size={{ xs: 12, md: 6 }} data-testid="client-custom-field">
          <Autocomplete
            name={CORPORATE_ENTITY}
            value={client[CORPORATE_ENTITY]}
            onChange={(_, option) =>
              handleBrokerInfOptionsChange(CORPORATE_ENTITY, option)
            }
            options={brokerClientInfoAvailableOptions?.corporateEntity || []}
            getOptionLabel={({ name }) => name || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                size="sm"
                error={!!client.errors[CORPORATE_ENTITY]}
                helperText={client.errors[CORPORATE_ENTITY]}
                required
                label="Corporate Entity"
                slotProps={{
                  ...params.inputProps,
                  autoComplete: "none",
                }}
              />
            )}
          />
        </Grid>
      )}
      {!!brokerClientInfoConfig[OFFICE] && (
        <Grid size={{ xs: 12, md: 6 }} data-testid="client-custom-field">
          <Autocomplete
            name={OFFICE}
            value={client[OFFICE]}
            onChange={(_, option) =>
              handleBrokerInfOptionsChange(OFFICE, option)
            }
            options={brokerClientInfoAvailableOptions?.officeLocation || []}
            getOptionLabel={({ name }) => name || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                size="sm"
                error={!!client.errors[OFFICE]}
                helperText={client.errors[OFFICE]}
                required
                label="Office Location"
                slotProps={{
                  ...params.inputProps,
                  autoComplete: "none",
                }}
              />
            )}
          />
        </Grid>
      )}
      {!!brokerClientInfoConfig[ONEDIGITAL_OFFICE] &&
        oneDigitalOfficeLocations?.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }} data-testid="client-custom-field">
            <Autocomplete
              name={ONEDIGITAL_OFFICE}
              value={selectedOneDigitalOffice}
              onChange={(_, option) =>
                handleBrokerInfOptionsChange(ONEDIGITAL_OFFICE, option)
              }
              options={oneDigitalOfficeLocations || []}
              getOptionLabel={({ location }) => location || ""}
              isOptionEqualToValue={(option, value) =>
                option.location === value.location
              }
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="sm"
                  error={!!client.errors[ONEDIGITAL_OFFICE]}
                  helperText={client.errors[ONEDIGITAL_OFFICE]}
                  required
                  label="ONEDIGITAL Office"
                  slotProps={{
                    ...params.inputProps,
                    autoComplete: "none",
                  }}
                />
              )}
            />
          </Grid>
        )}
    </>
  );
};

const filterStateOptions = (options, { inputValue }) => {
  const lowerInputValue = inputValue.toLowerCase();
  return options.filter(
    (option) =>
      option.name.toLowerCase().includes(lowerInputValue) ||
      option.abbreviation.toLowerCase().includes(lowerInputValue),
  );
};

export default ClientForm;
